import React, { useState, useEffect } from 'react';
import {
  Card,
  Select,
  DatePicker,
  Typography,
  Statistic,
  Row,
  Col,
  Space,
  Table,
  InputNumber,
  Button,
  message,
  Tooltip,
  Alert,
  Tag,
  Divider
} from 'antd';
import {
  DollarOutlined,
  LineChartOutlined,
  RiseOutlined,
  SyncOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import config from '../config';
import CostAnalyticsChart from './CostAnalyticsChart';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const CostAnalytics = () => {
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [timeframe, setTimeframe] = useState('30days');
  const [dateRange, setDateRange] = useState(null);
  const [selectedModels, setSelectedModels] = useState([]);
  const [costSettings, setCostSettings] = useState([]);
  const [editingCosts, setEditingCosts] = useState({});
  const [savingCosts, setSavingCosts] = useState(false);
  const [displayMetric, setDisplayMetric] = useState('cost');
  
  // New state variables for cost sync
  const [syncingCosts, setSyncingCosts] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => {
    fetchCostStatistics();
    fetchCostSettings();
    fetchLastSyncTime();
  }, [timeframe, dateRange]);

  const fetchLastSyncTime = async () => {
    try {
      const response = await axios.get(
        `${config.apiSettings.root}wp-ai-workflows/v1/cost-sync-info`,
        {
          headers: { 'X-WP-Nonce': config.apiSettings.nonce }
        }
      );
      
      if (response.data && response.data.last_sync_time) {
        setLastSyncTime(response.data.last_sync_time);
      }
    } catch (error) {
      console.error('Error fetching last sync time:', error);
    }
  };

  const fetchCostStatistics = async () => {
    setLoading(true);
    try {
      const params = { timeframe };
      if (dateRange) {
        params.start_date = dateRange[0].format('YYYY-MM-DD');
        params.end_date = dateRange[1].format('YYYY-MM-DD');
      }

      const response = await axios.get(
        `${config.apiSettings.root}wp-ai-workflows/v1/cost-statistics`,
        {
          params,
          headers: { 'X-WP-Nonce': config.apiSettings.nonce }
        }
      );
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching cost statistics:', error);
      message.error('Failed to fetch cost statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchCostSettings = async () => {
    try {
      const response = await axios.get(
        `${config.apiSettings.root}wp-ai-workflows/v1/cost-settings`,
        {
          headers: { 'X-WP-Nonce': config.apiSettings.nonce }
        }
      );
  
      // Validate and sanitize the response data
      let settings = response.data;
      if (!Array.isArray(settings)) {
        console.warn('Cost settings response is not an array:', settings);
        settings = [];
      }
  
      // Filter out any invalid entries
      settings = settings.filter(setting => 
        setting && 
        typeof setting === 'object' && 
        setting.id && 
        setting.provider && 
        setting.model
      );
  
      console.log('Processed cost settings:', settings);
      setCostSettings(settings);
    } catch (error) {
      console.error('Error fetching cost settings:', error);
      message.error('Failed to fetch cost settings');
      setCostSettings([]); // Set empty array on error
    }
  };

  const handleSyncCosts = async () => {
    setSyncingCosts(true);
    setSyncResult(null);
    
    try {
      const response = await axios.post(
        `${config.apiSettings.root}wp-ai-workflows/v1/sync-costs`,
        {},
        {
          headers: { 
            'X-WP-Nonce': config.apiSettings.nonce,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        setSyncResult({
          status: 'success',
          message: response.data.data.message,
          details: response.data.data
        });
        message.success('Models successfully synced from OpenRouter');
        fetchCostSettings();
        fetchLastSyncTime();
      } else {
        setSyncResult({
          status: 'error',
          message: response.data.data.message
        });
        message.error('Failed to sync costs from OpenRouter');
      }
    } catch (error) {
      console.error('Error syncing costs:', error);
      setSyncResult({
        status: 'error',
        message: 'Failed to sync costs from OpenRouter'
      });
      message.error('Failed to sync costs from OpenRouter');
    } finally {
      setSyncingCosts(false);
    }
  };

  const handleCostUpdate = async () => {
    setSavingCosts(true);
    try {
      const costsToUpdate = Object.keys(editingCosts)
        .map((id) => {
          const setting = costSettings.find(
            (s) => s.id.toString() === id.toString()
          );
          if (!setting) return null;

          const changes = editingCosts[id];
          return {
            provider: setting.provider,
            model: setting.model,
            input_cost:
              changes.input_cost !== undefined
                ? parseFloat(changes.input_cost)
                : parseFloat(setting.input_cost),
            output_cost:
              changes.output_cost !== undefined
                ? parseFloat(changes.output_cost)
                : parseFloat(setting.output_cost)
          };
        })
        .filter(Boolean);

      if (costsToUpdate.length === 0) {
        message.warning('No cost changes to save');
        setSavingCosts(false);
        return;
      }

      const response = await axios.put(
        `${config.apiSettings.root}wp-ai-workflows/v1/cost-settings`,
        costsToUpdate,
        {
          headers: {
            'X-WP-Nonce': config.apiSettings.nonce,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.updated_count > 0) {
        message.success(`Successfully updated ${response.data.updated_count} cost settings`);
        fetchCostSettings();
        setEditingCosts({});
      } else {
        message.warning('No cost settings were updated');
      }
    } catch (error) {
      console.error('Failed to update cost settings:', error.response?.data?.message || error.message);
      message.error('Failed to update cost settings');
    } finally {
      setSavingCosts(false);
    }
  };

  const calculateSelectedStats = () => {
    if (!statistics?.model_stats) {
      return { totalCost: 0, totalCalls: 0, totalTokens: 0 };
    }

    const relevantStats =
      selectedModels.length === 0
        ? statistics.model_stats
        : statistics.model_stats.filter((stat) =>
            selectedModels.includes(stat.model)
          );

    return {
      totalCost: relevantStats.reduce(
        (sum, stat) => sum + Number(stat.total_cost || 0),
        0
      ),
      totalCalls: relevantStats.reduce(
        (sum, stat) => sum + Number(stat.usage_count || 0),
        0
      ),
      totalTokens: relevantStats.reduce(
        (sum, stat) =>
          sum +
          Number(stat.total_prompt_tokens || 0) +
          Number(stat.total_completion_tokens || 0),
        0
      )
    };
  };

  const renderMetricsCards = () => {
    const stats = calculateSelectedStats();

    return (
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card
            hoverable
            onClick={() => setDisplayMetric('cost')}
            style={{
              cursor: 'pointer',
              borderColor: displayMetric === 'cost' ? '#1890ff' : undefined
            }}
          >
            <Statistic
              title={<Text>Total Cost</Text>}
              value={stats.totalCost}
              precision={4}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card
            hoverable
            onClick={() => setDisplayMetric('calls')}
            style={{
              cursor: 'pointer',
              borderColor: displayMetric === 'calls' ? '#1890ff' : undefined
            }}
          >
            <Statistic
              title={<Text>Total API Calls</Text>}
              value={stats.totalCalls}
              prefix={<LineChartOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card
            hoverable
            onClick={() => setDisplayMetric('tokens')}
            style={{
              cursor: 'pointer',
              borderColor: displayMetric === 'tokens' ? '#1890ff' : undefined
            }}
          >
            <Statistic
              title={<Text>Total Tokens</Text>}
              value={stats.totalTokens}
              prefix={<RiseOutlined />}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  const handleModelSelect = (model) => {
    if (selectedModels.includes(model)) {
      setSelectedModels([]);  // Clear selection if clicking selected model
    } else {
      setSelectedModels([model]);  // Select new model
    }
  };

  const validCostSettings = Array.isArray(costSettings) ? costSettings : [];

  const renderCostSyncSection = () => {
    return (
      <Card style={{ marginBottom: 16 }}>
        <Row align="middle" gutter={16}>
          <Col xs={24} sm={24} md={16} lg={16}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Space>
                <Title level={5} style={{ margin: 0 }}>OpenRouter Model Costs</Title>
                <Tooltip title="Sync price information directly from OpenRouter's API to ensure up-to-date costs for all models">
                  <InfoCircleOutlined style={{ color: '#1890ff' }} />
                </Tooltip>
              </Space>
              <Text type="secondary">
                Keep pricing information updated for accurate cost tracking and budget management.
                {lastSyncTime && (
                  <span> Last synced: <Text strong>{lastSyncTime}</Text></span>
                )}
              </Text>
              {syncResult && (
                <Alert
                  message={syncResult.status === 'success' ? "Sync Successful" : "Sync Failed"}
                  description={syncResult.message}
                  type={syncResult.status === 'success' ? "success" : "error"}
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
            </Space>
          </Col>
          <Col xs={24} sm={24} md={8} lg={8} style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              icon={<SyncOutlined />}
              onClick={handleSyncCosts}
              loading={syncingCosts}
              style={{ marginTop: { xs: 16, sm: 16, md: 0 } }}
            >
              Sync Model Costs
            </Button>
          </Col>
        </Row>
        {syncResult?.status === 'success' && syncResult?.details && (
          <Row style={{ marginTop: 16 }}>
            <Col span={24}>
              <Space>
                <Tag color="green">Added: {syncResult.details.models_added || 0}</Tag>
                <Tag color="blue">Updated: {syncResult.details.models_updated || 0}</Tag>
              </Space>
            </Col>
          </Row>
        )}
      </Card>
    );
  };

  const renderCostTable = () => {
    const columns = [
      {
        title: 'Provider',
        dataIndex: 'provider',
        key: 'provider',
        width: '15%',
        filters: [...new Set(validCostSettings.map((setting) => setting?.provider || '').filter(Boolean))].map((provider) => ({
          text: provider,
          value: provider
        })),
        onFilter: (value, record) => record?.provider === value
      },
      {
        title: 'Model',
        dataIndex: 'model',
        key: 'model',
        width: '35%',
        ellipsis: true,
        render: (text, record) => (
          <Space>
            {text}
            {record.provider === 'openrouter' && (
              <Tag color="blue">OpenRouter</Tag>
            )}
          </Space>
        )
      },
      {
        title: 'Input Cost (per 1M tokens)',
        dataIndex: 'input_cost',
        key: 'input_cost',
        width: '25%',
        render: (text, record) => (
          <InputNumber
            step="0.000001"
            value={editingCosts[record.id]?.input_cost ?? text}
            onChange={(value) => {
              setEditingCosts((prev) => ({
                ...prev,
                [record.id]: {
                  ...prev[record.id],
                  input_cost: value
                }
              }));
            }}
            style={{ width: '150px' }}
          />
        )
      },
      {
        title: 'Output Cost (per 1M tokens)',
        dataIndex: 'output_cost',
        key: 'output_cost',
        width: '25%',
        render: (text, record) => (
          <InputNumber
            step="0.000001"
            value={editingCosts[record.id]?.output_cost ?? text}
            onChange={(value) => {
              setEditingCosts((prev) => ({
                ...prev,
                [record.id]: {
                  ...prev[record.id],
                  output_cost: value
                }
              }));
            }}
            style={{ width: '150px' }}
          />
        )
      }
    ];

    return (
      <div>
        {loading ? (
          <Card loading={true} style={{ marginTop: 16 }} />
        ) : (
          <Card
            title="Model Cost Settings"
            style={{ marginTop: 16 }}
            extra={
              <Button
                type="primary"
                onClick={handleCostUpdate}
                loading={savingCosts}
                disabled={Object.keys(editingCosts).length === 0}
              >
                Save Changes
              </Button>
            }
          >
            <Table
              columns={columns}
              dataSource={validCostSettings}
              rowKey={record => record?.id || Math.random().toString()}
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
              size="middle"
              loading={loading}
              scroll={{ y: 400 }}
              locale={{ 
                emptyText: 'No cost settings available'
              }}
            />
          </Card>
        )}
      </div>
    );
  };

  if (!Array.isArray(costSettings)) {
    console.error('Invalid cost settings data:', costSettings);
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Text type="danger">Error loading cost settings. Please try refreshing the page.</Text>
          <Button 
            type="primary" 
            onClick={() => window.location.reload()}
            style={{ marginTop: '10px' }}
          >
            Refresh Page
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4}>Cost Analytics</Title>
        </Col>
        <Col>
          <Space>
            <Select
              value={timeframe}
              onChange={setTimeframe}
              style={{ width: 140 }}
              options={[
                { label: 'Last 7 days', value: '7days' },
                { label: 'Last 30 days', value: '30days' },
                { label: 'Last 90 days', value: '90days' },
                { label: 'Last year', value: 'year' },
                { label: 'Custom', value: 'custom' }
              ]}
            />
            {timeframe === 'custom' && (
              <RangePicker
                onChange={(dates) => setDateRange(dates)}
                style={{ width: 280 }}
              />
            )}
          </Space>
        </Col>
      </Row>

      {renderCostSyncSection()}
      {renderMetricsCards()}
      <CostAnalyticsChart
        data={statistics}
        displayMetric={displayMetric}
        selectedModels={selectedModels}
        loading={loading}
        onModelSelect={handleModelSelect}
      />
      {renderCostTable()}
    </div>
  );
};

export default CostAnalytics;